import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, deleteDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc } from 'firebase/firestore';
import { dateIdeaDoc, dateIdeasColl } from '../db';
import { Screen, UserProfile } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; backTo?: Screen; }

interface Idea {
  id: string;
  title: string;
  note: string;
  category: string;
  done: boolean;
  createdBy: string;
  createdByName: string;
}

const STARTERS = [
  { title: 'Telefonları bırakıp 40 dakika yürüyüş', category: 'Sakin', note: 'Sadece konuşma ve yürüyüş.' },
  { title: 'Evde mini sinema gecesi', category: 'Ev', note: 'Bir film, bir içecek, tek battaniye.' },
  { title: 'Hiç gitmediğimiz bir kafede kahve', category: 'Dışarı', note: 'Mekan keşfi + fotoğraf.' },
  { title: 'Birbirimize playlist hazırlama', category: 'Müzik', note: '5 şarkı, 5 küçük açıklama.' },
  { title: 'Gece tatlı almaya çıkma', category: 'Spontane', note: 'Plan yok, sadece çık.' },
  { title: 'İstanbul’da vapur + çay saati', category: 'Dışarı', note: 'Kadıköy-Eminönü hattı gibi sakin bir rota seçin.' },
  { title: 'Ankara’da kitapçı + kahve rotası', category: 'Dışarı', note: 'Birbirinize birer kitap seçin, sonra kahve.' },
  { title: 'İzmir’de gün batımı yürüyüşü', category: 'Sakin', note: 'Kordon, sahil ya da size yakın bir rota.' },
  { title: 'Türkçe nostalji şarkı gecesi', category: 'Müzik', note: 'Her biri için bir anı anlatın.' },
];

const CATEGORIES = ['Sakin', 'Ev', 'Dışarı', 'Müzik', 'Spontane', 'Özel'];

export default function DateIdeas({ user, navigate, backTo = 'us' }: Props) {
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const q = query(dateIdeasColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => setIdeas(snap.docs.map(d => ({ id: d.id, ...d.data() } as Idea))));
  }, [user.coupleId]);

  const suggestion = useMemo(() => STARTERS[Math.floor(Math.random() * STARTERS.length)], []);

  const add = async (starter?: typeof STARTERS[number]) => {
    const source = starter || { title, note, category };
    if (!source.title.trim()) return;
    await addDoc(dateIdeasColl(user.coupleId), {
      title: source.title.trim(),
      note: source.note.trim(),
      category: source.category,
      done: false,
      createdBy: user.uid,
      createdByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setTitle('');
    setNote('');
    setCategory(CATEGORIES[0]);
    setOpen(false);
  };

  const toggle = async (idea: Idea) => {
    await updateDoc(dateIdeaDoc(user.coupleId, idea.id), { done: !idea.done });
  };

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(backTo)}>←</button>
        <h1 style={styles.title}>Buluşma Fikirleri</h1>
      </div>

      <div className="feature-page">
        <section className="feature-hero-card">
          <p className="eyebrow">Bugün ne yapsak?</p>
          <h2>{suggestion.title}</h2>
          <span>{suggestion.note}</span>
          <div className="split-actions">
            <button className="feature-primary" onClick={() => add(suggestion)}>Listeye ekle</button>
            <button className="feature-secondary" onClick={() => setOpen(true)}>Kendi fikrim</button>
          </div>
        </section>

        <section className="idea-list">
          {ideas.map(idea => (
            <article key={idea.id} className={`idea-card ${idea.done ? 'done' : ''}`}>
              <button onClick={() => toggle(idea)}>{idea.done ? '✓' : ''}</button>
              <div>
                <small>{idea.category}</small>
                <h3>{idea.title}</h3>
                {idea.note && <p>{idea.note}</p>}
              </div>
              {idea.createdBy === user.uid && <button className="text-delete" onClick={() => deleteDoc(dateIdeaDoc(user.coupleId, idea.id))}>Sil</button>}
            </article>
          ))}
          {ideas.length === 0 && <div className="empty-state">Henüz fikir yok. İlk fikri öneriden ekleyebilirsin.</div>}
        </section>
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2>Randevu fikri</h2>
            <p className="modal-sub">Küçük fikirler büyük günlere dönüşür.</p>
            <input placeholder="Fikir" value={title} onChange={e => setTitle(e.target.value)} style={{ marginBottom: 10 }} />
            <select value={category} onChange={e => setCategory(e.target.value)} style={styles.select}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <textarea placeholder="Kısa not" value={note} onChange={e => setNote(e.target.value)} style={{ marginBottom: 12 }} />
            <button className="btn btn-primary" onClick={() => add()}>Ekle</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  select: {
    width: '100%',
    marginBottom: 10,
    padding: '14px 16px',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.09)',
    background: 'rgba(255,255,255,0.045)',
    color: 'white',
    fontFamily: 'Inter, sans-serif',
  },
};
